import { Description, List, Search as SearchIcon } from '@mui/icons-material';
import PersonIcon from '@mui/icons-material/Person';
import { InputAdornment, Stack, TextField } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import BayerTable, { type BayerTableColumn } from '/@/components/bayer/table';
import useDebounce from '/@/hooks/debounce/useDebounce';
import { useGrowers } from '/@/hooks/growers';
import { useValidatedType } from '/@/hooks/session';
import { _ } from '/@/locale';
import { MuiSelect } from '/@/mui/select';
import { getSessionCountry } from '/@/store/session/selectors';
import { getCrops } from '/@/views/dashboard/hooks/crops';
import type { GrowersTabType } from '../../..';
import { FIELDS_EMPTY_STATE } from '../../../growers/components/growers-list';
import CampaignRvRow from './components/campaign-row';
import RecommendationsRvRow from './components/recommendation-row';
import ReportsRvRow from './components/reports-row';

const RVsList = ({
    tab,
    orderBy: orderByProp,
    order: orderProp,
    forceEmptyState = false,
    year,
}: {
    tab: GrowersTabType;
    orderBy?: string;
    order?: 'asc' | 'desc';
    forceEmptyState?: boolean;
    year?: number;
}) => {
    const country = useSelector(getSessionCountry);

    // Table Management
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 1000);

    // current campaign filters
    const [cropFilter, setCropFilter] = useState('');
    const [fieldsFilter, setFieldsFilter] = useState('');
    // past reports filters
    const [cropFilterPastReports, setCropFilterPastReports] = useState('');

    // order by
    const [orderBy, setOrderBy] = useState(orderByProp || 'name');
    const [order, setOrder] = useState(orderProp || 'asc');

    // Table Queries
    const growersListQuery = useGrowers({
        page,
        pageSize,
        // node
        search: debouncedSearch || undefined,
        cmCrop: cropFilter || undefined,
        hasReports: Boolean(cropFilterPastReports) || undefined,
        hasFields: fieldsFilter || undefined,
        country: country.code,
        nodeType: 'rv',
        year: year || undefined,
        ordering: orderBy ? `${order === 'asc' ? '' : '-'}${orderBy}` : undefined,
        excludeTargetNode: true,
    });
    const growers = useMemo(() => {
        return growersListQuery.data?.results || [];
    }, [growersListQuery.data]);

    const userType = useValidatedType();

    const emptyState = useMemo(() => {
        const isFiltering = search || cropFilter || fieldsFilter;
        let emptyIcon: React.ReactNode;
        let emptyTitle = '';
        let emptySubtitle = '';
        let emptyButton: React.ReactNode;

        const stateForUserType =
            FIELDS_EMPTY_STATE[userType as keyof typeof FIELDS_EMPTY_STATE] || FIELDS_EMPTY_STATE.default;
        if (tab === 'current-campaign') {
            if (stateForUserType && 'current-campaign' in stateForUserType) {
                emptyTitle = stateForUserType['current-campaign'].title;
                emptySubtitle = stateForUserType['current-campaign'].subtitle;
                emptyIcon = <PersonIcon color='primary' sx={{ width: 32, height: 32 }} />;
            }
        } else if (tab === 'past-reports') {
            emptyIcon = <Description color='primary' sx={{ width: 32, height: 32 }} />;
            if (stateForUserType && 'past-reports' in stateForUserType) {
                emptyTitle = stateForUserType['past-reports'].title;
                emptySubtitle = stateForUserType['past-reports'].subtitle;
            }
        } else if (tab === 'recommendations') {
            if (stateForUserType && 'recommendations' in stateForUserType) {
                emptyTitle = stateForUserType['recommendations'].title;
                emptySubtitle = stateForUserType['recommendations'].subtitle;
                emptyIcon = <List color='primary' sx={{ width: 32, height: 32 }} />;
            }
        }

        if (isFiltering) {
            emptyTitle = _('No se encontraron resultados');
            emptySubtitle = '';
            emptyButton = undefined;
        }

        return {
            title: emptyTitle,
            subtitle: emptySubtitle,
            icon: emptyIcon,
            button: emptyButton,
        };
    }, [search, cropFilter, fieldsFilter, tab, year]);

    // Tabs Management
    const tabs: Record<GrowersTabType, { columns: BayerTableColumn[] }> = {
        'current-campaign': {
            columns: [
                { label: _('RV'), value: 'name', sortable: true, sortKey: 'name', sortOrder: 'asc' },
                { label: _('Productores'), value: 'growers' },
                { label: _('Lotes totales'), value: 'fields', sortable: true, sortKey: 'fields', sortOrder: 'asc' },
                { label: _('Distribución de Cultivos'), value: 'cropDistribution' },
                { label: '', value: 'actions' },
            ],
        },
        'past-reports': {
            columns: [
                { label: _('RV'), value: 'name', sortable: true, sortKey: 'name', sortOrder: 'asc' },
                { label: _('Productores'), value: 'growers' },
                { label: _('Lotes totales'), value: 'fields', sortable: true, sortKey: 'fields', sortOrder: 'asc' },
                {
                    label: _('Reportes generados'),
                    value: 'reports',
                    sortable: true,
                    sortKey: 'reports',
                },
                { label: '', value: 'actions' },
            ],
        },
        recommendations: {
            columns: [
                { label: _('RV'), value: 'name', sortable: true, sortKey: 'name', sortOrder: 'asc' },
                { label: _('Productores'), value: 'growers' },
                { label: _('Lotes totales'), value: 'fields', sortable: true, sortKey: 'fields', sortOrder: 'asc' },
                {
                    label: _('Recomendación híbridos'),
                    value: 'reports',
                    sortable: true,
                    sortKey: 'hybrid',
                },
                {
                    label: _('Recomendación protección'),
                    value: 'reports',
                    sortable: true,
                    sortKey: 'protection',
                },
                { label: '', value: 'actions' },
            ],
        },
    };
    const TableRows = useMemo(() => {
        return growers.map((grower) => {
            if (tab === 'current-campaign') {
                return <CampaignRvRow key={grower.id} rv={grower} cropFilter={cropFilter} />;
            }
            if (tab === 'past-reports') {
                return <ReportsRvRow key={grower.id} rv={grower} cropFilter={cropFilter} year={year} />;
            }
            if (tab === 'recommendations') {
                return <RecommendationsRvRow key={grower.id} rv={grower} cropFilter={cropFilter} year={year} />;
            }
            return null;
        });
    }, [growers, cropFilter, tab, year]);

    const crops = getCrops();
    const Filters = useMemo(() => {
        if (tab === 'current-campaign')
            return (
                <>
                    <Grid2 xs={6} md={3}>
                        <MuiSelect
                            inputLabel={_('Cultivos')}
                            value={cropFilter}
                            onChange={(e) => setCropFilter(e.target.value as string)}
                            options={[
                                { value: '', label: _('Todos') },
                                ...Object.values(crops).map((crop) => ({ value: crop.value, label: crop.name })),
                            ]}
                            formControlProps={{
                                fullWidth: true,
                            }}
                            clearable
                        />
                    </Grid2>
                    <Grid2 xs={6} md={3}>
                        <MuiSelect
                            inputLabel={_('Lotes')}
                            value={fieldsFilter}
                            onChange={(e) => setFieldsFilter(e.target.value as string)}
                            options={[
                                { value: '', label: _('Todos') },
                                { value: 'true', label: _('Con lotes') },
                                { value: 'false', label: _('Sin lotes') },
                            ]}
                            formControlProps={{
                                fullWidth: true,
                            }}
                            clearable
                        />
                    </Grid2>
                </>
            );
        if (tab === 'past-reports')
            return (
                <>
                    <Grid2 xs={6} md={3}>
                        <MuiSelect
                            inputLabel={_('Cultivos')}
                            value={cropFilterPastReports}
                            onChange={(e) => setCropFilterPastReports(e.target.value as string)}
                            options={[
                                { value: '', label: _('Todos') },
                                ...Object.values(crops).map((crop) => ({ value: crop.value, label: crop.name })),
                            ]}
                            formControlProps={{
                                fullWidth: true,
                            }}
                            clearable
                        />
                    </Grid2>
                    {/* <Grid2 xs={6} md={3}>
                        <MuiSelect
                            inputLabel={_('Tipo de siembra')}
                            value={fieldsFilter}
                            onChange={(e) => setFieldsFilter(e.target.value as string)}
                            options={[{ value: '', label: _('Todos') }]} // TODO: Add options based on Dashboard API?
                            formControlProps={{
                                fullWidth: true,
                            }}
                            clearable
                            disabled={cropFilter === ''}
                        />
                    </Grid2> */}
                </>
            );
        return null;
    }, [cropFilter, fieldsFilter, cropFilterPastReports, tab, crops]);

    return (
        <Stack spacing={3}>
            {/* Search and Filters */}
            <Grid2 container spacing={2} mx={'-8px !important'}>
                <Grid2 xs={12} md>
                    <TextField
                        label={_('Buscar un RV por nombre')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position='start'>
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        fullWidth
                    />
                </Grid2>
                {Filters}
            </Grid2>
            {/* Table */}
            <BayerTable
                columns={tabs[tab].columns}
                rows={TableRows}
                page={page}
                pageSize={pageSize}
                setPage={setPage}
                setPageSize={setPageSize}
                orderBy={orderBy}
                order={order}
                setOrderBy={setOrderBy}
                setOrder={setOrder}
                isValidating={growersListQuery.isLoading}
                count={growersListQuery.data?.count}
                emptyState={emptyState}
                forceEmptyState={forceEmptyState}
            />
        </Stack>
    );
};

export default RVsList;
